import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { BotaoCopiar } from './BotaoCopiar';
import { ActivityIndicator, Alert, Pressable, ScrollView, Switch, Text, View } from 'react-native';
import type { AbaId, Task } from '../store/types';
import { getDoc, setDoc } from '../store/store';
import { router } from 'expo-router';
import { newTask, saveTask } from '../store/planner';
import { ABAS, C, aba, alpha, useCores } from './theme';
import { Btn, Chip, Field, Sheet } from './ui';
import { PickerModal } from './Pickers';
import { useTaskSheet } from './TaskSheet';
import {
  alterarGrupo, apagarEnvio, apagarGrupo, criarGrupo, enviar, enviosDe, entrarGrupo, meuId, meuNome, membrosDe, meusGrupos, recebidos, setMeusAvisos,
  removerMembro, responder, sairGrupo, setAdmin, textoConvite, trocarCodigo, linkConvite, recarregarGrupos, setPapel, historicoDe,
  type Historico,
  type DadosEnvio, type Envio, type Grupo, type Membro, type Recebido, type TipoEnvio,
} from '../lib/grupos';
import { compartilharTexto } from '../lib/anexos';
import { abrirRota, abrirSite, juntarLocal, whatsapp } from '../lib/mapa';
import { semBloqueio } from '../auth/AuthContext';
import { brYmd, hm, horaTela, parseYmd, today, uid, ymd } from '../lib/date';
import { fusoAparelho } from '../lib/fuso';
import { t } from '../lib/i18n';
import { DEFS } from '../modulos/defs';
import { desligarCompartilhamento, fichasRaiz, ligarCompartilhamento, papeisDoGrupo, vincularFicha } from '../lib/compartilhar';
import { MOD_IDS, type ModId } from '../store/types';
import { cicloEstudosGrupo } from '../lib/estudosGrupo';
import { ROLAGEM_TECLADO } from './teclado';
import { useConfirmarIdentidade } from './ConfirmarIdentidade';

const nomeCol = (aba: string, col: string) => { if (col.endsWith('__nota')) return t('Nota rápida'); const d = (DEFS as any)[aba]; const c = d?.colecoes.find((x: any) => x.id === col); return c ? t(c.um) : col; };
const erro = (e: any) => Alert.alert(t('Não foi possível'), String(e?.message ?? e));
type MeuGrupo = Grupo & { admin: boolean; membros: number; papel: string; avisos?: boolean };

/** Cria o grupo já com as regras da aba (entre colegas todos veem e enviam; turma, pacientes e clientes só o administrador). */
export async function criarGrupoPara(abaId: AbaId, nome: string, ref?: string | null): Promise<MeuGrupo> {
  let g = await criarGrupo(abaId, nome, ref);
  if (!SO_ADMIN.includes(abaId)) { await alterarGrupo(g.id, { ver_membros: true, membros_enviam: true }).catch(() => {}); g = { ...g, ver_membros: true, membros_enviam: true }; }
  recarregarGrupos();
  return { ...g, admin: true, membros: 1, papel: 'membro' };
}
/** Pergunta e liga os dados compartilhados do grupo (a ficha de origem e tudo dentro dela, ou a aba inteira). */
export function confirmarCompartilhar(g: MeuGrupo, depois?: () => void) {
  const nomeAba = aba(g.aba)?.nome ?? '';
  Alert.alert(t('Compartilhar os dados com o grupo?'),
    (g.ref ? t('Tudo o que está dentro de "{nome}" passa a ser visto pelo grupo, cada pessoa conforme o papel que você escolher (quem entra começa com o papel mais limitado).', { nome: g.nome })
      : t('Os dados da aba {aba} passam a ser vistos pela equipe, cada pessoa conforme o papel que você escolher.', { aba: nomeAba }))
    + '\n\n' + t('Valores e dados clínicos só aparecem para quem o papel permitir. Você pode desligar quando quiser: os dados continuam com você.'), [
      { text: t('Cancelar'), style: 'cancel' },
      { text: t('Compartilhar'), onPress: async () => {
        try { const n = await ligarCompartilhamento(g); Alert.alert(t('Dados compartilhados'), t('{n} ficha(s) no grupo. Escolha o papel de cada pessoa em Membros.', { n })); depois?.(); }
        catch (e) { erro(e); }
      } }]);
}

/** Manda o convite do grupo (compartilhar ou WhatsApp). */
export async function convidarPara(g: Pick<Grupo, 'nome' | 'codigo'>, viaWhats = false, tel = '') {
  const txt = textoConvite(g, await meuNome());
  if (viaWhats) return whatsapp(tel, txt);
  try { if ((await semBloqueio(() => compartilharTexto(txt, g.nome))) === 'copiado') Alert.alert(t('Convite copiado. Cole onde quiser enviar.')); } catch (e) { erro(e); }
}
// nestas abas o grupo começa com só o administrador enviando (turma, pacientes, clientes); nas outras, entre colegas todos enviam
const SO_ADMIN: AbaId[] = ['professor', 'saude', 'freelancer', 'monitor', 'financas', 'documentos', 'consultorio', 'hospedagem', 'portal'];

/** Abrir os grupos: lista (de uma aba ou todos), um grupo já com o formulário de envio, ou os recebidos. */
export type AbrirGrupos = { aba?: AbaId; ref?: string; novo?: { nome: string; ref?: string | null }; envio?: { titulo: string; tipo?: TipoEnvio; dados: DadosEnvio }; recebidos?: boolean };
const Ctx = createContext<{ abrir: (a?: AbrirGrupos) => void } | null>(null);
export const useGrupos = () => { const c = useContext(Ctx); if (!c) throw new Error('GruposProvider ausente'); return c; };
let abrirGlobal: ((a?: AbrirGrupos) => void) | null = null;
/** Abre os grupos de qualquer lugar (inclusive da ficha da tarefa, que fica fora do provedor). */
export const abrirGrupos = (a?: AbrirGrupos) => abrirGlobal?.(a);

export function GruposProvider({ children }: { children: ReactNode }) {
  const [args, setArgs] = useState<AbrirGrupos | null>(null);
  const [k, setK] = useState(0);
  const abrir = useCallback((a: AbrirGrupos = {}) => { setArgs(a); setK(x => x + 1); }, []);
  useEffect(() => { abrirGlobal = abrir; return () => { abrirGlobal = null; }; }, [abrir]);
  return (
    <Ctx.Provider value={{ abrir }}>
      {children}
      {args && (args.recebidos ? <RecebidosSheet key={k} onClose={() => setArgs(null)} /> : <GruposSheet key={k} args={args} onClose={() => { setArgs(null); recarregarGrupos(); }} />)}
    </Ctx.Provider>
  );
}

// ─── Lista e detalhe dos grupos ──────────────────────────────────────────────────────────────────
function GruposSheet({ args, onClose }: { args: AbrirGrupos; onClose: () => void }) {
  const { accent, corDe } = useCores();
  const cor = args.aba ? corDe(args.aba) : accent;
  const [lista, setLista] = useState<MeuGrupo[] | null>(null);
  const [aberto, setAberto] = useState<MeuGrupo | null>(null);
  const [nome, setNome] = useState(args.novo?.nome ?? '');
  const [abaNovo, setAbaNovo] = useState<AbaId>(args.aba ?? 'planner');
  const [codigo, setCodigo] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  async function carregar() {
    try {
      const l = (await meusGrupos()).filter(g => !args.aba || g.aba === args.aba);
      setLista(l);
      // veio da ficha (ex.: a turma): abre o grupo dela, se já existir
      const ref = args.ref ?? args.novo?.ref;
      if (ref && !aberto) { const g = l.find(x => x.ref === ref); if (g) setAberto(g); }
      return l;
    } catch (e: any) { setMsg(String(e?.message ?? e)); setLista([]); return []; }
  }
  useEffect(() => { carregar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function criar() {
    if (!nome.trim()) return Alert.alert(t('Dê um nome ao grupo.'));
    setBusy(true);
    try {
      const g = await criarGrupoPara(abaNovo, nome.trim(), args.novo?.ref);
      await carregar(); setAberto(g); setNome('');
    }
    catch (e) { erro(e); } finally { setBusy(false); }
  }
  async function entrar() {
    if (codigo.trim().length < 6) return;
    setBusy(true);
    try { const g = await entrarGrupo(codigo); setCodigo(''); const l = await carregar(); Alert.alert(t('Você entrou no grupo'), g.nome); const x = l.find(y => y.id === g.id); if (x) setAberto(x); }
    catch (e) { erro(e); } finally { setBusy(false); }
  }

  if (aberto) return <GrupoDetalhe g={aberto} cor={corDe(aberto.aba) || cor} envio={args.envio} onVoltar={() => { setAberto(null); carregar(); }} onClose={onClose} />;
  return (
    <Sheet visible onClose={onClose} title={args.aba ? t('Grupos de {aba}', { aba: aba(args.aba).nome }) : t('Grupos e convites')}>
      <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 80 }}>
        {!!args.envio && <Text style={{ color: C.text, fontWeight: '700' }}>📤 {t('Escolha o grupo para enviar: {t}', { t: args.envio.titulo })}</Text>}
        {!args.envio && <Text style={{ color: C.sub, fontSize: 13 }}>{t('Crie um grupo (turma, pacientes, equipe, convidados, clientes), mande o link de convite e envie compromissos, materiais e avisos. Cada pessoa aceita, edita ou recusa o que recebe.')}</Text>}
        {!!msg && <Text style={{ color: C.warn }}>{msg}</Text>}
        {lista === null && <ActivityIndicator color={cor} />}
        {lista?.map(g => (
          <Pressable key={g.id} onPress={() => setAberto(g)} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: C.card, borderRadius: 14, padding: 12 }}>
            <Text style={{ fontSize: 24 }}>{aba(g.aba)?.icon ?? '👥'}</Text>
            <View style={{ flex: 1 }}>
              <Text style={{ color: C.text, fontWeight: '700' }}>{g.nome}</Text>
              <Text style={{ color: C.sub, fontSize: 12 }}>{[aba(g.aba)?.nome, t('{n} pessoa(s)', { n: g.membros }), g.admin ? t('você administra') : ''].filter(Boolean).join(' · ')}</Text>
            </View>
            <Text style={{ color: C.mute, fontSize: 20 }}>›</Text>
          </Pressable>
        ))}
        {lista && !lista.length && !msg && <Text style={{ color: C.mute }}>{t('Nenhum grupo ainda.')}</Text>}

        {!args.envio && (<>
          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 12 }}>{t('CRIAR UM GRUPO')}</Text>
          <Field value={nome} onChangeText={setNome} placeholder={t('Nome (ex.: 8º ano B, Pacientes da clínica, Aniversário da Ana)')} />
          {!args.aba && (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6 }}>
              {ABAS.map(a => <Chip key={a.id} small cor={cor} on={abaNovo === a.id} label={`${a.icon} ${a.curto}`} onPress={() => setAbaNovo(a.id)} />)}
            </ScrollView>
          )}
          <Btn cor={cor} label={busy ? t('Aguarde...') : '+ ' + t('Criar grupo')} disabled={busy} onPress={criar} />
          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 12 }}>{t('ENTRAR COM UM CÓDIGO')}</Text>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Field value={codigo} onChangeText={x => setCodigo(x.toUpperCase())} placeholder={t('Código do convite')} autoCapitalize="characters" autoCorrect={false} style={{ flex: 1, minWidth: 0 }} onSubmitEditing={entrar} />
            <Chip cor={cor} on={false} label={t('Entrar')} onPress={entrar} />
          </View>
        </>)}
      </ScrollView>
    </Sheet>
  );
}

function GrupoDetalhe({ g: g0, cor, envio, onVoltar, onClose }: { g: MeuGrupo; cor: string; envio?: AbrirGrupos['envio']; onVoltar: () => void; onClose: () => void }) {
  const [g, setG] = useState(g0);
  const ident = useConfirmarIdentidade(cor);   // texto 26 (B6, REGRAS 1.7): grupo é item grande (senha para apagar)
  const [membros, setMembros] = useState<Membro[]>([]);
  const [envios, setEnvios] = useState<Awaited<ReturnType<typeof enviosDe>>>([]);
  const [eu, setEu] = useState('');
  const [form, setForm] = useState(!!envio);
  const [hist, setHist] = useState<Historico[]>([]);
  const [papelDe, setPapelDe] = useState<string | null>(null);   // membro com a lista de papéis aberta
  const podeEnviar = g.admin || g.membros_enviam;
  const papeis = papeisDoGrupo(g);
  const temDados = Object.keys(papeis).length > 0;
  async function carregar() {
    try { setEu(await meuId()); setMembros(await membrosDe(g.id)); if (podeEnviar) setEnvios(await enviosDe(g.id)); } catch (e) { erro(e); }
    if (g.admin && g.compartilhar) historicoDe(g.id).then(setHist).catch(() => {});
  }
  const atualizarG = async () => { await recarregarGrupos(); const x = (await meusGrupos()).find(y => y.id === g.id); if (x) setG(x as MeuGrupo); carregar(); };
  useEffect(() => { carregar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const convidar = (viaWhats = false) => convidarPara(g, viaWhats);
  const mudar = async (p: Partial<Grupo>) => { setG(x => ({ ...x, ...p })); try { await alterarGrupo(g.id, p); } catch (e) { erro(e); } };

  if (form) return <EnvioForm g={g} cor={cor} membros={membros.filter(m => m.user_id !== eu)} inicial={envio}
    onFim={enviado => { if (enviado && envio) { onClose(); Alert.alert(t('Enviado'), t('Cada pessoa do grupo recebe e decide se aceita.')); } else { setForm(false); carregar(); } }} />;

  return (
    <Sheet visible onClose={onClose} title={g.nome} right={<Pressable onPress={onVoltar} hitSlop={10}><Text style={{ color: C.sub }}>{t('Grupos')}</Text></Pressable>}>
      <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 80 }}>
        {g.admin && (
          <View style={{ backgroundColor: alpha(cor, 0.14), borderRadius: 16, padding: 14, gap: 8, borderWidth: 1, borderColor: alpha(cor, 0.4) }}>
            <Text style={{ color: C.text, fontWeight: '800' }}>🔗 {t('Convite')}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }} selectable>{linkConvite(g.codigo)}</Text>
            <Text style={{ color: C.text, fontSize: 20, fontWeight: '800', letterSpacing: 3 }} selectable>{g.codigo}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{t('Quem abrir o link baixa o My Planner (ou usa no navegador), entra na conta e passa a fazer parte do grupo.')}</Text>
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
              <Chip cor={cor} on label={'📤 ' + t('Enviar convite')} onPress={() => convidar()} />
              <Chip cor={cor} on={false} label="💬 WhatsApp" onPress={() => convidar(true)} />
              <BotaoCopiar cor={cor} texto={g.codigo} rotulo={t('Copiar código')} />
              <BotaoCopiar cor={cor} texto={linkConvite(g.codigo)} rotulo={t('Copiar link')} />
              <Chip cor={cor} on={false} label={'♻️ ' + t('Trocar o código')} onPress={() => Alert.alert(t('Trocar o código?'), t('O link antigo deixa de funcionar. Quem já está no grupo continua.'), [
                { text: t('Cancelar'), style: 'cancel' }, { text: t('Trocar'), onPress: async () => { try { const c = await trocarCodigo(g.id); setG(x => ({ ...x, codigo: c })); } catch (e) { erro(e); } } }])} />
            </View>
          </View>
        )}
        {podeEnviar && <Btn cor={cor} label={'📤 ' + t('Enviar para o grupo')} onPress={() => setForm(true)} />}
        {g.aba === 'estudos' && <FicharioDoGrupoCartao g={g} cor={cor} onMudar={p => { setG(x => ({ ...x, ...p })); recarregarGrupos(); }} onAbrir={() => {
          onClose(); setTimeout(() => router.navigate({ pathname: '/aba/[id]', params: { id: 'estudos', item: 'grupo:' + g.id } } as any), 350); }} />}

        {temDados && (g.admin || g.compartilhar) && (
          <View style={{ backgroundColor: C.card, borderRadius: 16, padding: 14, gap: 8, borderWidth: 1, borderColor: g.compartilhar ? alpha(cor, 0.5) : C.line }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Text style={{ color: C.text, fontWeight: '800', flex: 1 }}>📂 {t('Dados compartilhados')}</Text>
              {g.admin && <Switch value={!!g.compartilhar} trackColor={{ true: cor }} onValueChange={v => {
                if (v) return confirmarCompartilhar(g, atualizarG);
                Alert.alert(t('Parar de compartilhar?'), t('Os outros membros deixam de ver os dados (a cópia some do dispositivo deles). Tudo continua com você.'), [
                  { text: t('Cancelar'), style: 'cancel' }, { text: t('Parar'), style: 'destructive', onPress: async () => { try { await desligarCompartilhamento(g); atualizarG(); } catch (e) { erro(e); } } }]);
              }} />}
            </View>
            <Text style={{ color: C.sub, fontSize: 12 }}>{g.compartilhar
              ? (g.admin ? t('Todos trabalham nos mesmos dados, cada um conforme o papel. Toque no papel de cada pessoa em Membros para trocar.')
                : t('Seu papel: {p}. {d}', { p: t(papeis[g.papel]?.nome ?? ''), d: t(papeis[g.papel]?.desc ?? '') }))
              : t('Ligue para o grupo trabalhar nos mesmos dados (obra, viagem, turma, imóvel, consultório), cada pessoa com o seu papel.')}</Text>
            {g.admin && g.compartilhar && Object.entries(papeis).filter(([k]) => k !== 'membro').map(([k, p]) => (
              <Text key={k} style={{ color: C.sub, fontSize: 12 }}>• <Text style={{ color: C.text, fontWeight: '700' }}>{t(p.nome)}</Text>: {t(p.desc)}</Text>
            ))}
          </View>
        )}

        {!g.admin && !g.compartilhar && temDados && (
          <View style={{ backgroundColor: alpha(C.warn, 0.12), borderRadius: 14, padding: 12, gap: 6 }}>
            {g.compartilhar === undefined
              ? <Text style={{ color: C.text, fontSize: 13 }}>⚙️ {t('O servidor ainda não foi preparado para dados compartilhados. No computador, abra a pasta C:\\MyPlanner e dê dois cliques em 15-LIGAR-GRUPOS-COLABORATIVOS.bat (uma vez só).')}</Text>
              : <Text style={{ color: C.text, fontSize: 13 }}>⏳ {t('Os dados deste grupo ainda não foram liberados. Peça para quem criou o grupo atualizar o My Planner e abrir o grupo: lá aparece o botão para liberar.')}</Text>}
          </View>
        )}
        {g.admin && !g.compartilhar && g.compartilhar !== undefined && !g.ref && (MOD_IDS as string[]).includes(g.aba) && (() => {
          const fichas = fichasRaiz(g.aba as ModId);
          return (
            <View style={{ backgroundColor: alpha(cor, 0.12), borderRadius: 16, padding: 14, gap: 8, borderWidth: 1, borderColor: alpha(cor, 0.4) }}>
              <Text style={{ color: C.text, fontWeight: '800' }}>📂 {t('Liberar para o grupo')}</Text>
              <Text style={{ color: C.sub, fontSize: 12 }}>{fichas.length ? t('Toque no que este grupo vai compartilhar. Tudo o que está dentro (roteiro, fotos, reservas...) vai junto e todos participam.')
                : t('Ainda não há nada nesta aba para compartilhar. Crie primeiro (ex.: a viagem) e volte aqui.')}</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                {fichas.map(f => <Chip key={f.r.id} cor={cor} on label={f.titulo || t('Sem nome')} onPress={async () => {
                  try { await vincularFicha(g, f.r, true); await atualizarG(); Alert.alert(t('Pronto'), t('"{n}" agora é do grupo. Em até 1 minuto os outros membros veem tudo.', { n: f.titulo })); } catch (e) { erro(e); } }} />)}
              </View>
            </View>
          );
        })()}
        {g.compartilhar && (
          <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 6 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Text style={{ color: C.text, flex: 1 }}>🔔 {t('Avisar no celular quando alguém incluir algo')}</Text>
              <Switch value={g.avisos !== false} trackColor={{ true: cor }} onValueChange={async v => {
                setG(x => ({ ...x, avisos: v }));
                try { await setMeusAvisos(g.id, v); recarregarGrupos(); } catch (e) { setG(x => ({ ...x, avisos: !v })); erro(e); } }} />
            </View>
            <Text style={{ color: C.mute, fontSize: 11 }}>{t('Vale só para você. No máximo um aviso a cada 3 minutos por pessoa, mesmo que ela inclua várias coisas.')}</Text>
          </View>
        )}

        {g.admin && (<>
          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 8 }}>{t('O QUE OS MEMBROS PODEM')}</Text>
          <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 10 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Text style={{ color: C.text, flex: 1 }}>{t('Ver a lista de membros')}</Text>
              <Switch value={g.ver_membros} onValueChange={v => mudar({ ver_membros: v })} trackColor={{ true: cor }} />
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Text style={{ color: C.text, flex: 1 }}>{t('Enviar para o grupo')}</Text>
              <Switch value={g.membros_enviam} onValueChange={v => mudar({ membros_enviam: v })} trackColor={{ true: cor }} />
            </View>
            <Text style={{ color: C.mute, fontSize: 11 }}>{t('Os membros sempre veem os administradores e só recebem o que foi enviado para eles.')}</Text>
          </View>
        </>)}

        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 8 }}>{t('MEMBROS ({n})', { n: membros.length })}</Text>
        {membros.map(m => (
          <View key={m.user_id} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: C.card, borderRadius: 12, padding: 10 }}>
            <Text style={{ fontSize: 18 }}>{m.admin ? '⭐' : '👤'}</Text>
            <View style={{ flex: 1, gap: 2 }}>
              <Text style={{ color: C.text }} numberOfLines={1}>{m.nome || t('Sem nome')}{m.user_id === eu ? ` (${t('você')})` : ''}</Text>
              {g.compartilhar && !m.admin && (
                <Pressable disabled={!g.admin} onPress={() => setPapelDe(papelDe === m.user_id ? null : m.user_id)} hitSlop={6}>
                  <Text style={{ color: g.admin ? cor : C.sub, fontSize: 12, fontWeight: '700' }}>{t(papeis[m.papel ?? 'membro']?.nome ?? '')}{g.admin ? ' ▾' : ''}</Text>
                </Pressable>
              )}
              {g.compartilhar && g.admin && papelDe === m.user_id && (
                <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                  {Object.entries(papeis).filter(([k]) => k !== 'membro').map(([k, p]) => (
                    <Chip key={k} small cor={cor} on={(m.papel ?? 'membro') === k} label={t(p.nome)} onPress={async () => {
                      try { await setPapel(g.id, m.user_id, k); setPapelDe(null); carregar(); } catch (e) { erro(e); } }} />
                  ))}
                </View>
              )}
            </View>
            {g.admin && m.user_id !== eu && m.user_id !== g.dono && (<>
              <Pressable onPress={async () => { try { await setAdmin(g.id, m.user_id, !m.admin); carregar(); } catch (e) { erro(e); } }} hitSlop={6}>
                <Text style={{ color: cor, fontSize: 12, fontWeight: '700' }}>{m.admin ? t('Tirar admin') : t('Tornar admin')}</Text>
              </Pressable>
              <Pressable onPress={() => Alert.alert(t('Remover {nome}?', { nome: m.nome }), t('A pessoa sai do grupo e deixa de receber os envios.'), [
                { text: t('Cancelar'), style: 'cancel' }, { text: t('Remover'), style: 'destructive', onPress: async () => { try { await removerMembro(g.id, m.user_id); carregar(); } catch (e) { erro(e); } } }])} hitSlop={6}>
                <Text style={{ color: C.danger, fontSize: 16 }}>✕</Text>
              </Pressable>
            </>)}
          </View>
        ))}
        {g.admin && membros.length <= 1 && <Text style={{ color: C.mute, fontSize: 12 }}>{t('Ninguém entrou ainda. Envie o convite.')}</Text>}

        {podeEnviar && !!envios.length && (<>
          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 8 }}>{t('ENVIADOS')}</Text>
          {envios.map(e => (
            <View key={e.id} style={{ backgroundColor: C.card, borderRadius: 12, padding: 10, gap: 4 }}>
              <Text style={{ color: C.text, fontWeight: '700' }}>{iconeTipo(e.tipo)} {e.titulo}</Text>
              <Text style={{ color: C.sub, fontSize: 12 }}>{[e.dados.data && brYmd(e.dados.data), e.dados.hora && horaTela(e.dados.hora), e.para ? t('{n} pessoa(s)', { n: e.para.length }) : t('todos')].filter(Boolean).join(' · ')}</Text>
              <Text style={{ color: C.sub, fontSize: 12 }}>✅ {e.aceitos} · ❌ {e.recusados} · ⏳ {e.pendentes}</Text>
              {(g.admin || e.de === eu) && <Pressable onPress={async () => { try { await apagarEnvio(e.id); carregar(); } catch (x) { erro(x); } }}><Text style={{ color: C.danger, fontSize: 12 }}>{t('Apagar envio')}</Text></Pressable>}
            </View>
          ))}
        </>)}

        {g.admin && g.compartilhar && !!hist.length && (<>
          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 8 }}>{t('HISTÓRICO DOS DADOS')}</Text>
          {hist.slice(0, 30).map((h, i) => (
            <Text key={i} style={{ color: C.sub, fontSize: 12 }}>{new Date(h.em).toLocaleString(undefined, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} · <Text style={{ color: C.text }}>{h.nome || '?'}</Text> {t(h.acao === 'criou' ? 'incluiu' : h.acao === 'apagou' ? 'apagou' : h.acao === 'ligou' ? 'ligou o compartilhamento' : 'alterou')} {h.col ? `(${nomeCol(g.aba, h.col)})` : ''}</Text>
          ))}
        </>)}

        {g.dono === eu ? (
          <Btn outline cor={C.danger} label={t('Apagar o grupo')} style={{ marginTop: 16 }} onPress={() => Alert.alert(t('Apagar o grupo?'), t('Todos saem do grupo. O que cada pessoa já aceitou continua na agenda dela.'), [
            { text: t('Cancelar'), style: 'cancel' }, { text: t('Apagar'), style: 'destructive', onPress: async () => { if (!(await ident.pedir(t('Confirme para apagar o grupo')))) return; try { await apagarGrupo(g.id); onVoltar(); } catch (e) { erro(e); } } }])} />
        ) : (
          <Btn outline cor={C.danger} label={t('Sair do grupo')} style={{ marginTop: 16 }} onPress={() => Alert.alert(t('Sair do grupo?'), '', [
            { text: t('Cancelar'), style: 'cancel' }, { text: t('Sair'), style: 'destructive', onPress: async () => { try { await sairGrupo(g.id); onVoltar(); } catch (e) { erro(e); } } }])} />
        )}
      </ScrollView>
      {ident.tela}
    </Sheet>
  );
}

/** 0.71 (texto 09 J/K5): o fichário que o grupo de estudos vê. O dono escolhe (ou troca) o fichário; os colegas abrem para ler. */
function FicharioDoGrupoCartao({ g, cor, onMudar, onAbrir }: { g: MeuGrupo; cor: string; onMudar: (p: Partial<Grupo>) => void; onAbrir: () => void }) {
  const [trocar, setTrocar] = useState(false);
  const caixa = { backgroundColor: alpha(cor, 0.12), borderRadius: 16, padding: 14, gap: 8, borderWidth: 1, borderColor: alpha(cor, 0.4) } as const;
  if (!g.admin) return (
    <View style={caixa}>
      <Text style={{ color: C.text, fontWeight: '800' }}>📚 {t('Fichário do grupo')}</Text>
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('Você vê na tela o que foi compartilhado neste grupo, para ler e acompanhar.')}</Text>
      <Btn cor={cor} label={'📚 ' + t('Abrir o fichário do grupo')} onPress={onAbrir} />
    </View>
  );
  const ficharios = getDoc('estudos')?.ficharios ?? [];
  const atual = g.ref ? ficharios.find(f => f.id === g.ref) : undefined;
  const escolher = async (ref: string | null) => { try { await alterarGrupo(g.id, { ref }); onMudar({ ref }); setTrocar(false); await recarregarGrupos(); cicloEstudosGrupo(); } catch (e) { erro(e); } };
  return (
    <View style={caixa}>
      <Text style={{ color: C.text, fontWeight: '800' }}>📚 {t('Fichário do grupo')}</Text>
      {g.ref && !atual && <Text style={{ color: C.sub, fontSize: 12 }}>{t('O fichário deste grupo está em outro dispositivo seu. Ele continua compartilhado a partir de lá.')}</Text>}
      {!!atual && <Text style={{ color: C.text }}>{atual.icon} {atual.nome}</Text>}
      <Text style={{ color: C.sub, fontSize: 12 }}>{g.ref
        ? t('Os colegas veem este fichário na tela, só para ler: não editam, não baixam, não imprimem e não compartilham. As suas metas, o cronômetro e as páginas revisadas continuam só seus.')
        : t('Escolha o fichário que os colegas vão ver na tela (só para ler).')}</Text>
      {(!g.ref || trocar) && (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          {ficharios.map(f => <Chip key={f.id} small cor={cor} on={f.id === g.ref} label={`${f.icon} ${f.nome}`} onPress={() => escolher(f.id)} />)}
          {!ficharios.length && <Text style={{ color: C.mute, fontSize: 12 }}>{t('Crie um fichário em Estudos e volte aqui.')}</Text>}
        </View>
      )}
      {!!g.ref && (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          {!trocar && <Chip small cor={cor} on={false} label={t('Trocar fichário')} onPress={() => setTrocar(true)} />}
          <Chip small cor={cor} on={false} label={t('Parar de compartilhar')} onPress={() => Alert.alert(t('Parar de compartilhar?'), t('Os colegas deixam de ver o fichário. Tudo continua com você.'), [
            { text: t('Cancelar'), style: 'cancel' }, { text: t('Parar'), style: 'destructive', onPress: () => escolher(null) }])} />
        </View>
      )}
    </View>
  );
}

export const iconeTipo = (tp: TipoEnvio) => (tp === 'compromisso' ? '📅' : tp === 'material' ? '📚' : '📢');

function EnvioForm({ g, cor, membros, inicial, onFim }: { g: MeuGrupo; cor: string; membros: Membro[]; inicial?: AbrirGrupos['envio']; onFim: (enviado: boolean) => void }) {
  const [tipo, setTipo] = useState<TipoEnvio>(inicial?.tipo ?? (inicial?.dados.data ? 'compromisso' : 'aviso'));
  const [titulo, setTitulo] = useState(inicial?.titulo ?? '');
  const [d, setD] = useState<DadosEnvio>(inicial?.dados ?? {});
  const [links, setLinks] = useState((inicial?.dados.links ?? []).join('\n'));
  const [para, setPara] = useState<string[] | null>(null);
  const [picker, setPicker] = useState<'date' | 'time' | null>(null);
  const [busy, setBusy] = useState(false);
  async function mandar() {
    if (!titulo.trim()) return Alert.alert(t('Escreva o título.'));
    if (tipo === 'compromisso' && !d.data) return Alert.alert(t('Escolha a data do compromisso.'));
    if (para && !para.length) return Alert.alert(t('Escolha para quem enviar.'));
    setBusy(true);
    try {
      const dados: DadosEnvio = { ...d, fuso: d.hora ? d.fuso || fusoAparelho() : undefined, links: links.split(/\s+/).map(x => x.trim()).filter(x => /^https?:\/\//i.test(x)) };
      await enviar(g.id, tipo, titulo.trim(), dados, para);
      onFim(true);
    } catch (e) { erro(e); } finally { setBusy(false); }
  }
  const TIPOS: [TipoEnvio, string][] = [['compromisso', '📅 ' + t('Compromisso')], ['material', '📚 ' + t('Matéria ou material')], ['aviso', '📢 ' + t('Aviso')]];
  return (
    <Sheet visible onClose={() => onFim(false)} title={t('Enviar para {g}', { g: g.nome })}>
      <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 80 }}>
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>{TIPOS.map(([k, l]) => <Chip key={k} cor={cor} on={tipo === k} label={l} onPress={() => setTipo(k)} />)}</View>
        <Field value={titulo} onChangeText={setTitulo} placeholder={tipo === 'compromisso' ? t('Ex.: Aula de revisão, Consulta de retorno') : t('Título')} />
        {tipo === 'compromisso' && (<>
          <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
            <Chip cor={cor} on={!!d.data} label={`📅 ${d.data ? brYmd(d.data) : t('Escolher data')}`} onPress={() => setPicker('date')} />
            <Chip cor={cor} on={!!d.hora} label={`🕐 ${d.hora ? horaTela(d.hora) : t('Escolher hora')}`} onPress={() => setPicker('time')} />
            {!!d.hora && <Chip cor={cor} on={false} label={t('Sem horário')} onPress={() => setD(x => ({ ...x, hora: undefined }))} />}
          </View>
          {!!d.hora && <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>{[30, 45, 50, 60, 90, 120].map(m => <Chip key={m} small cor={cor} on={(d.duracao ?? 60) === m} label={`${m} min`} onPress={() => setD(x => ({ ...x, duracao: m }))} />)}</View>}
          <Field value={d.local ?? ''} onChangeText={v => setD(x => ({ ...x, local: v }))} placeholder={t('Endereço (se for presencial)')} />
        </>)}
        <Field value={d.link ?? ''} onChangeText={v => setD(x => ({ ...x, link: v }))} placeholder={t('Link da aula ou reunião online (opcional)')} autoCapitalize="none" autoCorrect={false} />
        <Field multiline value={d.texto ?? ''} onChangeText={v => setD(x => ({ ...x, texto: v }))} placeholder={tipo === 'material' ? t('Conteúdo, matéria, tarefa de casa...') : t('Mensagem ou observações')} style={{ minHeight: 100, textAlignVertical: 'top' }} />
        <Field multiline semDitado value={links} onChangeText={setLinks} placeholder={t('Links de materiais (Drive, vídeos, PDFs), um por linha')} autoCapitalize="none" autoCorrect={false} style={{ minHeight: 60, textAlignVertical: 'top' }} />
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 6 }}>{t('PARA QUEM')}</Text>
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
          <Chip cor={cor} on={para === null} label={t('Todos do grupo')} onPress={() => setPara(null)} />
          <Chip cor={cor} on={para !== null} label={t('Escolher pessoas')} onPress={() => setPara(p => p ?? [])} />
        </View>
        {para !== null && (
          <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
            {membros.map(m => <Chip key={m.user_id} small cor={cor} on={para.includes(m.user_id)} label={m.nome || '?'} onPress={() => setPara(p => (p!.includes(m.user_id) ? p!.filter(x => x !== m.user_id) : [...p!, m.user_id]))} />)}
            {!membros.length && <Text style={{ color: C.mute, fontSize: 12 }}>{t('Você não vê os outros membros deste grupo.')}</Text>}
          </View>
        )}
        <Btn cor={cor} label={busy ? t('Enviando...') : '📤 ' + t('Enviar')} disabled={busy} onPress={mandar} style={{ marginTop: 8 }} />
        <Text style={{ color: C.mute, fontSize: 11 }}>{t('Nada entra na agenda de ninguém sem autorização: cada pessoa aceita, edita ou recusa.')}</Text>
        {picker && <PickerModal mode={picker} cor={cor}
          value={picker === 'date' ? parseYmd(d.data || today()) : (() => { const x = new Date(); const [h, mi] = (d.hora || '08:00').split(':').map(Number); x.setHours(h, mi, 0, 0); return x; })()}
          onPick={x => setD(y => (picker === 'date' ? { ...y, data: ymd(x) } : { ...y, hora: hm(x), duracao: y.duracao ?? 60 }))} onClose={() => setPicker(null)} />}
      </ScrollView>
    </Sheet>
  );
}

// ─── Recebidos ───────────────────────────────────────────────────────────────────────────────────
/** Monta a tarefa do My Planner a partir do que chegou. */
export function tarefaDe(r: Recebido): Partial<Task> {
  const e = r.envio, d = e.dados;
  const aba = (r.grupo?.aba && ABAS.some(a => a.id === r.grupo!.aba) ? r.grupo.aba : 'planner') as AbaId;
  const notas = [d.texto, d.link ? `${t('Link')}: ${d.link}` : '', ...(d.links ?? []), `${t('Enviado por {quem} no grupo {g}', { quem: e.de_nome || '?', g: r.grupo?.nome ?? '' })}`].filter(Boolean).join('\n');
  return { title: e.titulo, emoji: d.emoji || '📩', emojiManual: true, area: aba, start: d.data || today(), time: d.hora || null,
    tz: d.hora ? d.fuso || null : null, duration: d.duracao || 60, notes: juntarLocal(d.local ?? '', notas), energy: 2 };
}

function RecebidosSheet({ onClose }: { onClose: () => void }) {
  const { accent, corDe } = useCores();
  const sheet = useTaskSheet();
  const [lista, setLista] = useState<Recebido[] | null>(null);
  const [msg, setMsg] = useState('');
  const carregar = () => recebidos().then(setLista).catch(e => { setMsg(String(e?.message ?? e)); setLista([]); });
  useEffect(() => { carregar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function aceitar(r: Recebido) {
    try {
      if (r.envio.tipo === 'compromisso') saveTask(newTask(tarefaDe(r)));
      else {
        // material e aviso viram uma caixa nas Anotações, com os links
        const d = r.envio.dados;
        const texto = [d.texto, d.local ? `📍 ${d.local}` : '', d.link, ...(d.links ?? []), `— ${r.envio.de_nome} · ${r.grupo?.nome ?? ''}`].filter(Boolean).join('\n');
        setDoc('anotacoes', a => ({ ...a, boxes: [{ id: uid(), title: `${iconeTipo(r.envio.tipo)} ${r.envio.titulo}`, aba: 'anotacoes', createdAt: new Date().toISOString(),
          blocks: [{ id: uid(), type: 'text', items: [], text: texto }] }, ...a.boxes] }));
      }
      await responder(r.envio.id, 'aceito');
      carregar();
    } catch (e) { erro(e); }
  }
  function editar(r: Recebido) {
    // fecha esta janela e abre a ficha da tarefa já preenchida (no iPhone, uma janela de cada vez)
    onClose();
    setTimeout(() => sheet.open({ prefill: tarefaDe(r), date: r.envio.dados.data, onSaved: () => { responder(r.envio.id, 'editado').catch(() => {}); } }), 400);
  }
  async function recusar(r: Recebido) { try { await responder(r.envio.id, 'recusado'); carregar(); } catch (e) { erro(e); } }

  const pend = (lista ?? []).filter(r => r.status === 'pendente'), resto = (lista ?? []).filter(r => r.status !== 'pendente');
  const Cartao = ({ r }: { r: Recebido }) => {
    const e = r.envio, d = e.dados, cor = r.grupo ? corDe(r.grupo.aba) || accent : accent;
    return (
      <View style={{ backgroundColor: C.card, borderRadius: 16, padding: 14, gap: 6, borderLeftWidth: 4, borderLeftColor: cor, opacity: r.status === 'pendente' ? 1 : 0.6 }}>
        <Text style={{ color: C.sub, fontSize: 12 }}>{aba(r.grupo?.aba as AbaId)?.icon ?? '👥'} {r.grupo?.nome} · {e.de_nome}</Text>
        <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>{iconeTipo(e.tipo)} {e.titulo}</Text>
        {!!d.data && <Text style={{ color: C.text }}>📅 {brYmd(d.data)}{d.hora ? ` · ${horaTela(d.hora)}` : ''}{d.duracao && d.hora ? ` · ${d.duracao} min` : ''}</Text>}
        {!!d.local && <Pressable onPress={() => abrirRota(d.local!)}><Text style={{ color: cor }}>📍 {d.local} · {t('Rota')}</Text></Pressable>}
        {!!d.link && <Pressable onPress={() => abrirSite(d.link!)}><Text style={{ color: cor }} numberOfLines={1}>🎥 {d.link}</Text></Pressable>}
        {!!d.texto && <Text style={{ color: C.sub }}>{d.texto}</Text>}
        {(d.links ?? []).map(l => <Pressable key={l} onPress={() => abrirSite(l)}><Text style={{ color: cor }} numberOfLines={1}>🔗 {l}</Text></Pressable>)}
        {r.status === 'pendente' ? (
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 4 }}>
            <Pressable onPress={() => aceitar(r)} style={{ flex: 1, backgroundColor: cor, borderRadius: 12, padding: 10, alignItems: 'center' }}><Text style={{ color: '#fff', fontWeight: '800' }}>✓ {t('Aceitar')}</Text></Pressable>
            {e.tipo === 'compromisso' && <Pressable onPress={() => editar(r)} style={{ flex: 1, borderWidth: 1, borderColor: cor, borderRadius: 12, padding: 10, alignItems: 'center' }}><Text style={{ color: cor, fontWeight: '800' }}>✏️ {t('Editar')}</Text></Pressable>}
            <Pressable onPress={() => recusar(r)} style={{ flex: 1, borderWidth: 1, borderColor: C.line, borderRadius: 12, padding: 10, alignItems: 'center' }}><Text style={{ color: C.sub, fontWeight: '700' }}>{t('Recusar')}</Text></Pressable>
          </View>
        ) : <Text style={{ color: C.mute, fontSize: 12 }}>{r.status === 'recusado' ? '❌ ' + t('Recusado') : r.status === 'editado' ? '✏️ ' + t('Aceito com alterações') : '✅ ' + t('Aceito')}</Text>}
      </View>
    );
  };
  return (
    <Sheet visible onClose={onClose} title={t('Recebidos')}>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 80 }}>
        <Text style={{ color: C.sub, fontSize: 13 }}>{t('O que os seus grupos enviaram. Compromissos aceitos vão para o My Planner; materiais e avisos vão para as Anotações.')}</Text>
        {!!msg && <Text style={{ color: C.warn }}>{msg}</Text>}
        {lista === null && <ActivityIndicator color={accent} />}
        {lista && !lista.length && !msg && <Text style={{ color: C.mute, textAlign: 'center', marginTop: 20 }}>{t('Nada recebido ainda.')}</Text>}
        {pend.map(r => <Cartao key={r.envio.id} r={r} />)}
        {!!resto.length && <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 8 }}>{t('JÁ RESPONDIDOS')}</Text>}
        {resto.map(r => <Cartao key={r.envio.id} r={r} />)}
      </ScrollView>
    </Sheet>
  );
}
